import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

const FilePreviewModal = ({ file, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mime = file?.mime_type || '';
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf' || file?.filename?.toLowerCase().endsWith('.pdf');
  const isGoogleDoc = mime.startsWith('application/vnd.google-apps.');
  const canPreview = isImage || isPdf || isGoogleDoc;

  useEffect(() => {
    if (!file || !canPreview) { setLoading(false); return; }
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await axios.get(`${API}/drive/file/${file.file_id}`, { responseType: 'blob' });
        if (cancelled) return;
        currentUrl = URL.createObjectURL(r.data);
        setBlobUrl(currentUrl);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Impossible de charger le fichier');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [file, canPreview]);

  if (!file) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="preview-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {file.icon && <img src={file.icon} alt="" className="w-5 h-5 shrink-0" />}
            <h3 className="font-semibold text-gray-900 truncate">{file.filename}</h3>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a href={file.url} target="_blank" rel="noreferrer" className="text-sm text-[#008A8D] hover:underline flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              Ouvrir dans Drive
            </a>
            {blobUrl && <a href={blobUrl} download={file.filename} className="text-sm text-gray-600 hover:text-gray-900">Telecharger</a>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none w-8 h-8 flex items-center justify-center" data-testid="close-preview">&times;</button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden bg-gray-50 flex items-center justify-center min-h-[400px]">
          {!canPreview ? (
            <div className="text-center p-8">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              <p className="text-gray-500 mb-4">Apercu non disponible pour ce type de fichier</p>
              <a href={file.url} target="_blank" rel="noreferrer" className="realease-btn-primary inline-flex items-center gap-2">Ouvrir dans Drive</a>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <div className="w-10 h-10 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin"></div>
              <p className="text-sm">Chargement de l'apercu...</p>
            </div>
          ) : error ? (
            <div className="text-center p-8">
              <p className="text-red-600 mb-4">{error}</p>
              <a href={file.url} target="_blank" rel="noreferrer" className="realease-btn-primary inline-flex items-center gap-2">Ouvrir dans Drive</a>
            </div>
          ) : isImage ? (
            <img src={blobUrl} alt={file.filename} className="max-w-full max-h-full object-contain" data-testid="preview-image" />
          ) : (
            <iframe src={blobUrl} title={file.filename} className="w-full h-full border-0" data-testid="preview-iframe" />
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
